import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button, DialogOverlay, DialogOverlayActions, H4, Paragraph, Separator } from '@fs/zion-ui'
import { ADD_EXISTING_PERSON_ENTRY_POINTS } from '../utils/addExistingPersonConstants'

/**
 * Confirms the relationships a move is about to break.
 *
 * Fires once per action rather than once per person. Callers decide whether it applies at all, with
 * {@link ../utils/atRiskRelationships}.hasAtRiskRelationships.
 *
 * @param {Object} params
 * @param {Object} params.overlay - Controlled overlay from the flow; its `close` reports a dismissal
 *   rather than performing one, so X, Escape and click-away all arrive as `onCancel`
 * @param {Array<import('../utils/atRiskRelationships').AtRiskRelationshipGroup>} params.atRiskGroups
 *   The relationships that actually break, grouped by moving person
 * @param {string} params.entryPoint - One of {@link ADD_EXISTING_PERSON_ENTRY_POINTS}; picks the closing clause
 * @param {string} [params.focusPersonName] - Focus person's name, required for the relationships-card wording
 * @param {() => void} [params.onCancel] - Called when the patron backs out, returning them to the
 *   selection sheet with their picks intact rather than abandoning the flow
 * @param {() => void} [params.onContinue] - Called when the patron accepts the removals
 * @returns {React.ReactElement}
 */
const PreviousRelationshipsDialog = ({
  overlay,
  atRiskGroups = [],
  entryPoint,
  focusPersonName,
  onCancel = () => {},
  onContinue = () => {},
}) => {
  const [t] = useTranslation()

  const isSinglePerson = atRiskGroups.length === 1
  const openingCopy = isSinglePerson
    ? t(
        'add-existing-person.previous-relationships.body.one',
        '{name} is in another record with existing relationships.',
        { name: atRiskGroups[0]?.personName }
      )
    : t(
        'add-existing-person.previous-relationships.body.several',
        'The people below are in another record with existing relationships.'
      )

  const closingClause =
    entryPoint === ADD_EXISTING_PERSON_ENTRY_POINTS.RELATIONSHIPS_CARD
      ? t(
          'add-existing-person.previous-relationships.clause.existing-group',
          "To add them to {focusPersonName}'s group, the following relationships will be removed:",
          { focusPersonName }
        )
      : t(
          'add-existing-person.previous-relationships.clause.new-group',
          'To move them into a new group, the following relationships will be removed:'
        )

  return (
    <DialogOverlay
      {...overlay}
      title={t('add-existing-person.previous-relationships.title', 'Previous Relationships')}
      data-testid="previous-relationships-dialog"
      size="sm"
      footer={
        <DialogOverlayActions>
          <Button
            color="gray"
            onClick={onCancel}
            linkName="Previous Relationships Cancel"
            data-testid="previous-relationships-cancel-button"
          >
            {t('add-existing-person.previous-relationships.cancel.button', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            onClick={onContinue}
            linkName="Previous Relationships Remove"
            data-testid="previous-relationships-remove-button"
          >
            {t('add-existing-person.previous-relationships.remove.button', 'Remove Relationships')}
          </Button>
        </DialogOverlayActions>
      }
    >
      <Paragraph size="sm">{`${openingCopy} ${closingClause}`}</Paragraph>
      <Separator />
      {atRiskGroups.map((group) => (
        <React.Fragment key={group.personId}>
          <H4 size="H6">{group.personName}</H4>
          <Separator size="xxs" />
          <ul data-testid={`at-risk-relationships-${group.personId}`}>
            {group.relationships.map((relationship) => (
              <li key={relationship.id}>
                <Paragraph size="sm">
                  {/* Hyphen separator, matching the removal confirmation elsewhere in the flow. */}
                  {`${relationship.counterpartName} - ${relationship.relationshipLabel}`}
                </Paragraph>
              </li>
            ))}
          </ul>
          <Separator />
        </React.Fragment>
      ))}
      <Paragraph size="xs" secondary>
        {t(
          'add-existing-person.previous-relationships.other-groups-unaffected',
          'Relationships in other groups are not affected.'
        )}
      </Paragraph>
    </DialogOverlay>
  )
}

export default PreviousRelationshipsDialog
